import { FunctionComponent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    ButtonGroup,
    Card,
    CardContent,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Drawer,
    Fade,
    IconButton,
    TextField,
    Typography,
} from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import { Language } from '@starjack/utils';
import { createEmptyFaq, Faq, FaqGroupStructure } from '../model/faq.types';
import { FaqDrawerContent } from './faqDrawerContent';
import { useGetFaqsQuery, useDeleteFaqMutation } from '@starjack/entities/faq';
import { useNotification } from '@starjack/features/notifications';
import { LoadingContainer } from '@starjack/shared-components';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import { CardListRow } from '@starjack/shared-components';
import { BoldLabel } from '@starjack/shared-components';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import TrashIcon from '@heroicons/react/24/solid/TrashIcon';
import { LoadingButton } from '@mui/lab';

type ButtonGroupValue = 'german' | 'english';

type FaqsConfigurationProps = {
    canMutateFaq?: (faq: Faq) => boolean;
    liftOperatorId?: string;
};

export const FaqsConfiguration: FunctionComponent<FaqsConfigurationProps> = ({
    canMutateFaq,
    liftOperatorId,
}) => {
    const [buttonGroupValue, setButtonGroupValue] =
        useState<ButtonGroupValue>('german');
    const [searchValue, setSearchValue] = useState<string>('');
    const [faqsConfig, setFaqsConfig] = useState<{
        isOpen: boolean;
        faq: Faq;
    }>({ isOpen: false, faq: null });
    const [faqToDelete, setFaqToDelete] = useState<Faq | null>(null);
    const [deleteFaqId, setDeleteFaqId] = useState<string | null>(null);

    const { t } = useTranslation();
    const { addNotification } = useNotification();
    const [deleteFaq] = useDeleteFaqMutation();

    const currentLanguage: Language =
        buttonGroupValue === 'german' ? Language.DE : Language.EN;

    // Single query fetches all FAQs, no language param
    const { data: allFaqs = [] } = useGetFaqsQuery({ liftOperatorId });

    const filteredFaqs = useMemo(() => {
        const lang = currentLanguage === Language.DE ? 'de' : 'en';
        return filterFaqsByMultiLang(allFaqs, searchValue, lang);
    }, [currentLanguage, allFaqs, searchValue]);

    const onButtonGroupChange = (val: ButtonGroupValue) => () => {
        setButtonGroupValue(val);
    };

    const onDrawerOpenClick = (faq: Faq | null = null) => {
        setFaqsConfig({
            isOpen: true,
            faq,
        });
    };

    const onDrawerClose = () => {
        setFaqsConfig({
            isOpen: false,
            faq: null,
        });
    };

    const onConfirmDelete = () => {
        if (!faqToDelete) return;
        setDeleteFaqId(faqToDelete.id);
        deleteFaq({ faqId: faqToDelete.id })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: t('faq.deleteSuccess'),
                });
                setFaqToDelete(null);
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message: t('faq.deleteError'),
                });
            })
            .finally(() => {
                setDeleteFaqId(null);
            });
    };

    return (
        <Container
            sx={{
                mt: 3,
                ml: 'unset',
            }}
            maxWidth='lg'
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    mb: 2,
                }}
            >
                <Button variant='contained' onClick={() => onDrawerOpenClick()}>
                    + neue FAQ erstellen
                </Button>
            </Box>
            <Card
                sx={{
                    mb: 2,
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                    }}
                >
                    <TextField
                        sx={{
                            width: '50%',
                        }}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                        }}
                        label='Nach FAQ suchen'
                    />

                    <ButtonGroup
                        sx={{
                            ml: 'auto',
                        }}
                    >
                        <Button
                            onClick={onButtonGroupChange('german')}
                            variant={
                                buttonGroupValue === 'german'
                                    ? 'contained'
                                    : 'outlined'
                            }
                        >
                            DE
                        </Button>
                        <Button
                            onClick={onButtonGroupChange('english')}
                            variant={
                                buttonGroupValue === 'english'
                                    ? 'contained'
                                    : 'outlined'
                            }
                        >
                            EN
                        </Button>
                    </ButtonGroup>
                </CardContent>
            </Card>

            <Box>
                {filteredFaqs?.map((group) => (
                    <Accordion
                        key={group.id}
                        sx={{
                            borderRadius: '2px',
                            p: 4,
                        }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography variant='h6'>
                                {group.name[
                                    currentLanguage === Language.DE
                                        ? 'de'
                                        : 'en'
                                ] || (
                                    <Typography
                                        sx={{
                                            fontStyle: 'italic',
                                            color: 'text.secondary',
                                        }}
                                    >
                                        Kategoriename fehlt
                                    </Typography>
                                )}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails
                            sx={{
                                p: 0,
                            }}
                        >
                            {group.faqs.length === 0 && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        width: '100%',
                                        py: 4,
                                    }}
                                >
                                    <Typography>
                                        Keine FAQs dieser Kategorie zugeordnet
                                    </Typography>
                                </Box>
                            )}

                            {group.faqs.map((item) => {
                                const isMutable =
                                    !canMutateFaq || canMutateFaq(item);
                                return (
                                    <CardListRow
                                        key={item.id}
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            cursor: isMutable
                                                ? 'pointer'
                                                : 'default',
                                            borderRadius: '2px',
                                            transition: 'all 0.3s ease',
                                            ...(isMutable && {
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                    backgroundColor:
                                                        'action.hover',
                                                },
                                            }),
                                        }}
                                        onClick={
                                            isMutable
                                                ? () => onDrawerOpenClick(item)
                                                : undefined
                                        }
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                width: '100%',
                                                gap: 2,
                                            }}
                                        >
                                            <Typography>
                                                <BoldLabel>Frage:</BoldLabel>{' '}
                                                {item.titleMulti[
                                                    currentLanguage ===
                                                    Language.DE
                                                        ? 'de'
                                                        : 'en'
                                                ] || (
                                                    <Typography
                                                        component='span'
                                                        sx={{
                                                            fontStyle: 'italic',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        (Titel nicht verfügbar)
                                                    </Typography>
                                                )}
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    width: '90%',
                                                    ...(!searchValue.length && {
                                                        textOverflow:
                                                            'ellipsis',
                                                        overflow: 'hidden',
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient:
                                                            'vertical',
                                                    }),
                                                }}
                                            >
                                                <BoldLabel>Antwort:</BoldLabel>{' '}
                                                {item.textMulti[
                                                    currentLanguage ===
                                                    Language.DE
                                                        ? 'de'
                                                        : 'en'
                                                ] || (
                                                    <Typography
                                                        component='span'
                                                        sx={{
                                                            fontStyle: 'italic',
                                                            color: 'text.secondary',
                                                        }}
                                                    >
                                                        (Text nicht verfügbar)
                                                    </Typography>
                                                )}
                                            </Typography>

                                            <Typography>
                                                <BoldLabel>Prio:</BoldLabel>{' '}
                                                {item.priority}
                                            </Typography>
                                        </Box>

                                        {isMutable && (
                                            <ArrowForwardIosIcon
                                                sx={{
                                                    ml: 0.5,
                                                }}
                                            />
                                        )}
                                        {isMutable && (
                                            <IconButton
                                                size='small'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setFaqToDelete(item);
                                                }}
                                                sx={{ ml: 'auto' }}
                                            >
                                                <SvgIcon fontSize='medium'>
                                                    <TrashIcon />
                                                </SvgIcon>
                                            </IconButton>
                                        )}
                                        <Fade in={deleteFaqId === item.id}>
                                            <div>
                                                <LoadingContainer cover />
                                            </div>
                                        </Fade>
                                    </CardListRow>
                                );
                            })}
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>

            <Drawer
                anchor='right'
                open={faqsConfig.isOpen}
                onClose={onDrawerClose}
            >
                <FaqDrawerContent
                    onClose={onDrawerClose}
                    faq={faqsConfig?.faq || createEmptyFaq(liftOperatorId)}
                    canMutateFaq={canMutateFaq}
                />
            </Drawer>

            <Dialog open={!!faqToDelete} onClose={() => setFaqToDelete(null)}>
                <DialogTitle>{t('faq.deleteDialogTitle')}</DialogTitle>
                <DialogContent>{t('faq.deleteDialogContent')}</DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setFaqToDelete(null)}
                        variant='outlined'
                    >
                        {t('faq.cancelButton')}
                    </Button>
                    <LoadingButton
                        loading={!!deleteFaqId}
                        onClick={onConfirmDelete}
                        color='error'
                        variant='contained'
                    >
                        {t('faq.deleteButton')}
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const filterFaqsByMultiLang = (
    faqResponse: FaqGroupStructure,
    searchValue: string,
    lang: 'de' | 'en'
): FaqGroupStructure => {
    if (!faqResponse.length) {
        return [];
    }

    if (!searchValue) {
        return faqResponse;
    }

    return faqResponse.reduce<FaqGroupStructure>((faqGroups, currentValue) => {
        const filteredFaqs = currentValue.faqs.filter((faq) => {
            const title = faq.titleMulti[lang] || '';
            const text = faq.textMulti[lang] || '';
            return (
                title.toLowerCase().includes(searchValue.toLowerCase()) ||
                text.toLowerCase().includes(searchValue.toLowerCase())
            );
        });

        if (filteredFaqs.length) {
            faqGroups.push({
                ...currentValue,
                faqs: filteredFaqs,
            });
        }

        return faqGroups;
    }, []);
};
